/**
 * useNetworkHelpers — fetch the per-network helper inventory from
 * root via the BFF's `/api/networks/{network_id}/helpers` endpoint
 * (slug [[root-node-type]]).
 *
 * Inventory is sourced from NodeService.get_helpers, served by the
 * network's root node. Each entry carries:
 *   { module, display_name, kind, description, size, sha256, last_modified }
 *
 * Returns `{ helpers, loading, error, refresh }`:
 *   - helpers: array of entries (empty while loading or on error).
 *   - loading: true while a fetch is in flight.
 *   - error:   error string (null on success).
 *   - refresh: callable to force a re-fetch (bypasses the BFF's 60s
 *              cache only by triggering a new request; the BFF's
 *              in-process cache still hits if not expired).
 *
 * Usage in AddNodeModal: the parent node always belongs to a network,
 * so `networkId` is always non-null when the modal opens. A null
 * `networkId` (defensive) yields the no-op {helpers: [], loading: false}.
 */
import { useCallback, useEffect, useState } from 'react';

export function useNetworkHelpers(networkId) {
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (networkId == null || networkId === '') {
      setHelpers([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/networks/${encodeURIComponent(String(networkId))}/helpers`)
      .then((r) => {
        if (!r.ok) {
          return r
            .json()
            .catch(() => ({ error: `HTTP ${r.status}` }))
            .then((body) => {
              throw new Error(body.error || `HTTP ${r.status}`);
            });
        }
        return r.json();
      })
      .then((body) => {
        if (cancelled) return;
        // NodeService.get_helpers returns { helpers: [...] }.
        // The BFF passes it through verbatim.
        const list = Array.isArray(body?.helpers) ? body.helpers : [];
        setHelpers(list);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e.message || e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [networkId, refreshKey]);

  return { helpers, loading, error, refresh };
}
